import { Link, useParams } from 'react-router';

import { useAnalytics } from '@boc/analyticsV3';
import { useCart } from '@boc/framework/checkout/cart/provider';
import { useCart as useCartClient } from '@boc/framework/checkout/cart/use-cart';
import { TwPicture } from '@boc/images';
import { getAttributeValue } from '@boc/framework/products/attributes';
import type { ProductVariantCardFragment } from '@boc/framework/saleor-graphql';
import { cn } from '@boc/ui';

import toast from '~/toast';
import { absoluteify, imgUrl, urls } from '~/urls';

import { Price } from '~/local/components/price';
import { ProductTag } from '~/local/products/details/components/product-tag';
import { getVariantDisplayName, getVariantUrl } from '~/local/products/variant-url';

import { isOwnProduct } from '#/products/own';
import { genProductCategoryTitle, genProductTitle } from '#/products/seo';

const isVariantAvailable = (variant: ProductVariantCardFragment): boolean => {
  const product = variant.product;

  if (product.isAvailable === false) return false;

  return variant.quantityAvailable == null || variant.quantityAvailable > 0;
};

const VariantCardDetails = ({ variant }: { variant: ProductVariantCardFragment }) => {
  const product = variant.product;
  const displayName = getVariantDisplayName(variant);

  switch (product.productType.slug) {
    case 'jigsaw-puzzle': {
      const publisher = getAttributeValue(product.attributes, 'puzzle-publisher');

      return (
        <>
          <h5 className="text-base font-medium">{genProductCategoryTitle(product)}</h5>
          <div className="space-y-1">
            <h4 className="font-heading text-medium leading-5 tracking-tight">{displayName}</h4>
            {publisher ? <h5 className="font-heading text-base leading-3">{publisher}</h5> : <></>}
          </div>
          <div className="text-medium space-x-2 leading-5 font-medium tracking-wide">
            {product.pricing?.onSale ? (
              <Price price={variant.pricing?.priceUndiscounted?.gross} className="line-through" />
            ) : (
              <></>
            )}
            <Price price={variant.pricing?.price?.gross} />
          </div>
        </>
      );
    }
    default:
      return (
        <>
          <h5 className="font-medium">{genProductCategoryTitle(product)}</h5>
          <h4 className="font-heading text-medium leading-5 tracking-tight">{displayName}</h4>
          <div className="text-medium leading-5 font-medium tracking-wide">
            <Price price={variant.pricing?.price?.gross} />
          </div>
        </>
      );
  }
};

const VariantCardAddToCart = ({
  variant,
  onAddToCart,
  isAddingItem,
}: {
  variant: ProductVariantCardFragment;
  onAddToCart?: () => Promise<void>;
  isAddingItem?: boolean;
}) => {
  const variantAvailable = isVariantAvailable(variant);
  const isDisabled = isAddingItem || !variantAvailable;
  const showAddToCart = onAddToCart && variantAvailable;

  if (!showAddToCart) return null;

  return (
    <div className="flex justify-center px-4 pt-4">
      <button
        type="button"
        className="hover:bg-primary-2-20 border-primary-2-40 text-primary-2-40 w-fit cursor-pointer rounded-full border px-4 py-2 text-sm font-medium shadow-lg transition-all duration-300 ease-in-out hover:font-semibold disabled:cursor-not-allowed disabled:opacity-50 lg:-translate-y-full lg:opacity-0 lg:group-hover:translate-y-0 lg:group-hover:opacity-100"
        onClick={onAddToCart}
        disabled={isDisabled}
      >
        {isAddingItem ? 'Adding...' : 'Add to Cart'}
      </button>
    </div>
  );
};

function VariantCardInner({
  variant,
  showDiscount,
  artStyle = 'art',
}: {
  variant: ProductVariantCardFragment;
  showDiscount?: boolean;
  artStyle?: 'art' | 'box';
}) {
  const product = variant.product;
  const media = variant.media?.length ? variant.media : product.media;

  let mediaIndex = 0;

  if (artStyle === 'art') {
    const mainImageIndex =
      media?.findIndex((img) => img?.url && img.alt?.toLowerCase() === 'main-image') ?? -1;

    mediaIndex = mainImageIndex >= 0 ? mainImageIndex : 0;
  } else if (artStyle === 'box' && media && media.length > 1) {
    mediaIndex = 1;
  }

  const src = media?.[mediaIndex]?.url || product.thumbnail?.url;
  const alt = `${genProductTitle(product)} - ${variant.name} - Thumbnail Image`;

  return (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          'bg-primary-2-20 relative flex aspect-square items-center',
          !isVariantAvailable(variant) && 'opacity-60'
        )}
      >
        <ProductTag showDiscount={showDiscount} variant={variant} />
        <div className="mx-auto h-full w-full p-4">
          {src && (
            <TwPicture
              imgUrl={imgUrl}
              src={src}
              defaultSize={181}
              twSizes={{
                sm: 250,
                md: 310,
                lg: 250,
                xl: 310,
              }}
              alt={alt}
              className="mx-auto h-full w-full object-contain mix-blend-multiply"
            />
          )}
        </div>
        {isOwnProduct(product) && (
          <img
            className="absolute bottom-2 left-2 w-10 sm:bottom-3 sm:left-3 sm:w-12 md:bottom-4 md:left-4 md:w-14 lg:w-16 xl:w-20"
            src="/static/jsn-og-sticker.svg"
            alt={`JSN Original ${product.category?.name}`}
          />
        )}
      </div>
      <div className="text-primary-2-80 flex flex-col items-center gap-y-3 py-6 text-center">
        <VariantCardDetails variant={variant} />
      </div>
    </div>
  );
}

function VariantCard({
  showDiscount,
  variant,
  prefetch = false,
  artStyle,
}: {
  variant: ProductVariantCardFragment;
  prefetch?: boolean;
  showDiscount?: boolean;
  artStyle?: 'art' | 'box';
}) {
  const params = useParams();
  const baseUrl = urls.products.details(params, { slug: variant.product.slug });
  const detailsUrl = getVariantUrl(variant, baseUrl);

  const { addItem, isAddingItem } = useCartClient();
  const { cart } = useCart();
  const tracker = useAnalytics();

  const handleAddToCart = async () => {
    try {
      await addItem(variant.id, 1);

      tracker.trackAddToCart({
        variant: { ...variant, product: variant.product },
        quantity: 1,
        itemUrl: (v) => absoluteify(urls.products.details(params, { slug: v.product.slug })),
        checkout: cart,
      });

      toast.success(
        <div className="flex gap-12">
          <div>Your item has been added to cart</div>
          <Link to={urls.checkout.cart(params)} className="underline">
            Go to cart
          </Link>
        </div>,
        { id: `add-to-cart-success-${variant.id}` }
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add product to cart';
      const isInsufficientStock = errorMessage.includes('INSUFFICIENT_STOCK');

      toast.error(
        isInsufficientStock
          ? 'Not enough stock available for this item'
          : 'Failed to add product to cart',
        { id: `add-to-cart-error-${variant.id}` }
      );
    }
  };

  return (
    <>
      {/* Desktop */}
      <div className="group hidden h-full flex-col lg:flex">
        <Link to={detailsUrl} prefetch={prefetch ? 'render' : 'intent'}>
          <VariantCardInner showDiscount={showDiscount} variant={variant} artStyle={artStyle} />
        </Link>
        {isVariantAvailable(variant) && (
          <div className="relative overflow-hidden">
            <div className="h-0 transition-all duration-300 ease-in-out group-hover:h-16">
              <VariantCardAddToCart
                variant={variant}
                onAddToCart={handleAddToCart}
                isAddingItem={isAddingItem}
              />
            </div>
          </div>
        )}
      </div>

      {/* Mobile */}
      <div className="h-full lg:hidden">
        <Link to={detailsUrl} prefetch={prefetch ? 'render' : 'viewport'} className="block">
          <VariantCardInner showDiscount={showDiscount} variant={variant} artStyle={artStyle} />
        </Link>
        {isVariantAvailable(variant) && (
          <VariantCardAddToCart
            variant={variant}
            onAddToCart={handleAddToCart}
            isAddingItem={isAddingItem}
          />
        )}
      </div>
    </>
  );
}

export default VariantCard;
